import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAdminFetch } from "../../admin/hooks/useAdminFetch";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { getStakeholderLaneAppearance } from "../config/stakeholderLaneConfig";
import {
  STAKEHOLDER_CONTACT_ACTIONS,
  STAKEHOLDER_FOLLOW_UP_TYPES,
  STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS,
} from "../config/stakeholderContactActions";
import {
  deriveOutreachReasons,
  isOutreachQueueItem,
  matchesOutreachQueueFilter,
  OUTREACH_QUEUE_FILTERS,
} from "../utils/outreachQueue";
import { sortItemsGlobally } from "../utils/stakeholderPriority";

const safeJsonParse = async (response, fallback) => {
  try {
    return JSON.parse(await response.text());
  } catch {
    return fallback;
  }
};

const formatApiError = (payload, fallback) => {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return (
      detail
        .map((entry) => {
          if (typeof entry === "string") return entry;
          if (entry && typeof entry === "object") {
            const path = Array.isArray(entry.loc) ? entry.loc.slice(1).join(".") : "";
            const message = entry.msg || "";
            return path && message ? `${path}: ${message}` : message || path || "";
          }
          return "";
        })
        .filter(Boolean)
        .join("; ") || fallback
    );
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDispositionState = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

function OutreachQueueCard({ item, onOpen, onOpenTimeline, onRefresh, selected, onToggleSelect, batchActive }) {
  const adminFetch = useAdminFetch();
  const appearance = getStakeholderLaneAppearance(item.lane);
  const outreachReasons = deriveOutreachReasons(item);
  const disablePersonRouteActions = !!item.disable_person_route_actions;
  const [followUpDate, setFollowUpDate] = useState(item.follow_up_date || "");
  const [followUpType, setFollowUpType] = useState(item.follow_up_type || "");
  const [followUpNote, setFollowUpNote] = useState(item.follow_up_note || "");
  const [urgencyOverride, setUrgencyOverride] = useState(item.urgency_override || "auto");
  const [contactNote, setContactNote] = useState("");
  const [savingAction, setSavingAction] = useState("");
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [savingUrgency, setSavingUrgency] = useState(false);
  const [editingLatest, setEditingLatest] = useState(false);
  const [latestActionDraft, setLatestActionDraft] = useState(item.latest_contact_action || "");
  const [latestNoteDraft, setLatestNoteDraft] = useState(item.latest_contact_note || "");
  const [savingLatest, setSavingLatest] = useState(false);
  const [deletingLatest, setDeletingLatest] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [dispositionEntries, setDispositionEntries] = useState([]);
  const [dispositionLoading, setDispositionLoading] = useState(false);
  const [dispositionLoaded, setDispositionLoaded] = useState(false);
  const [dispositionError, setDispositionError] = useState("");

  const contextItems = useMemo(
    () =>
      [
        { label: "Why now", value: item.what_this_person_needs_now || item.reason || "" },
        { label: "Next move", value: item.next_move || "" },
        { label: "Follow-up note", value: item.follow_up_note || "" },
        { label: "Last movement", value: item.last_meaningful_movement || "" },
        { label: "Latest note", value: item.latest_contact_note || "" },
        { label: "Stakeholder notes", value: item.notes || "" },
      ].filter((entry) => entry.value),
    [
      item.follow_up_note,
      item.last_meaningful_movement,
      item.latest_contact_note,
      item.next_move,
      item.notes,
      item.reason,
      item.what_this_person_needs_now,
    ]
  );
  const hasDispositionContext =
    !!item.stakeholder_disposition_previous_state && !!item.stakeholder_disposition_new_state;
  const hasContext = !!contextItems.length || hasDispositionContext || !!item.display_detail_tags?.length;
  const canLoadDispositionPreview = !!item.source_type && !!item.source_id;

  const loadTimelinePreview = useCallback(async () => {
    if (!item.id) return;
    setTimelineLoading(true);
    setTimelineError("");
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}?limit=2`);
      const payload = await safeJsonParse(response, { entries: [] });
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to load recent touches"));
      }
      setTimelineEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setTimelineLoaded(true);
    } catch (error) {
      setTimelineError(error?.message || "Failed to load recent touches");
    } finally {
      setTimelineLoading(false);
    }
  }, [adminFetch, item.id]);

  const loadDispositionPreview = useCallback(async () => {
    if (!item.source_type || !item.source_id) return;
    setDispositionLoading(true);
    setDispositionError("");
    try {
      const params = new URLSearchParams({
        source_type: item.source_type,
        source_id: item.source_id,
        limit: "2",
      });
      const response = await adminFetch(`/admin/stakeholder/dispositions/history?${params.toString()}`);
      const payload = await safeJsonParse(response, { entries: [] });
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to load disposition history"));
      }
      setDispositionEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setDispositionLoaded(true);
    } catch (error) {
      setDispositionError(error?.message || "Failed to load disposition history");
    } finally {
      setDispositionLoading(false);
    }
  }, [adminFetch, item.source_id, item.source_type]);

  useEffect(() => {
    setFollowUpDate(item.follow_up_date || "");
    setFollowUpType(item.follow_up_type || "");
    setFollowUpNote(item.follow_up_note || "");
    setUrgencyOverride(item.urgency_override || "auto");
    setLatestActionDraft(item.latest_contact_action || "");
    setLatestNoteDraft(item.latest_contact_note || "");
    if (!item.latest_contact_entry_id) {
      setEditingLatest(false);
    }
  }, [
    item.follow_up_date,
    item.follow_up_note,
    item.follow_up_type,
    item.urgency_override,
    item.latest_contact_action,
    item.latest_contact_note,
    item.latest_contact_entry_id,
  ]);

  useEffect(() => {
    setTimelineEntries([]);
    setTimelineLoaded(false);
    setTimelineError("");
  }, [item.id]);

  useEffect(() => {
    setDispositionEntries([]);
    setDispositionLoaded(false);
    setDispositionError("");
  }, [item.source_id, item.source_type]);

  useEffect(() => {
    if (showContext && !timelineLoaded && !timelineLoading) {
      loadTimelinePreview();
    }
  }, [showContext, timelineLoaded, timelineLoading, loadTimelinePreview]);

  useEffect(() => {
    if (showContext && canLoadDispositionPreview && !dispositionLoaded && !dispositionLoading) {
      loadDispositionPreview();
    }
  }, [
    showContext,
    canLoadDispositionPreview,
    dispositionLoaded,
    dispositionLoading,
    loadDispositionPreview,
  ]);

  const handleLogAction = async (actionType) => {
    setSavingAction(actionType);
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lane: item.lane,
          title_at_time: item.title,
          action_type: actionType,
          note: contactNote,
          contact_value: item.contact_label || "",
        }),
      });
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to save contact action"));
      }
      const nextEntry = payload?.entry || null;
      if (nextEntry) {
        setTimelineEntries((current) => [nextEntry, ...current].slice(0, 2));
        setTimelineLoaded(true);
      }
      await onRefresh();
      toast.success(`${item.title}: ${payload?.entry?.action_label || "Contact action"} logged`);
      setContactNote("");
    } catch (error) {
      toast.error(error?.message || "Failed to save contact action");
    } finally {
      setSavingAction("");
    }
  };

  const handleSaveFollowUp = async () => {
    setSavingFollowUp(true);
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          follow_up_date: followUpDate,
          follow_up_type: followUpType,
          follow_up_note: followUpNote,
          urgency_override: urgencyOverride === "auto" ? "" : urgencyOverride,
        }),
      });
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to save follow-up plan"));
      }
      await onRefresh();
      toast.success(`${item.title}: follow-up saved`);
    } catch (error) {
      toast.error(error?.message || "Failed to save follow-up plan");
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleClearFollowUp = async () => {
    setSavingFollowUp(true);
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          follow_up_date: "",
          follow_up_type: "",
          follow_up_note: "",
          urgency_override: urgencyOverride === "auto" ? "" : urgencyOverride,
        }),
      });
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to clear follow-up plan"));
      }
      setFollowUpDate("");
      setFollowUpType("");
      setFollowUpNote("");
      await onRefresh();
      toast.success(`${item.title}: follow-up cleared`);
    } catch (error) {
      toast.error(error?.message || "Failed to clear follow-up plan");
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleSaveUrgency = async () => {
    setSavingUrgency(true);
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          follow_up_date: item.follow_up_date || "",
          follow_up_type: item.follow_up_type || "",
          follow_up_note: item.follow_up_note || "",
          urgency_override: urgencyOverride === "auto" ? "" : urgencyOverride,
        }),
      });
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to save urgency override"));
      }
      await onRefresh();
      toast.success(`${item.title}: urgency saved`);
    } catch (error) {
      toast.error(error?.message || "Failed to save urgency override");
    } finally {
      setSavingUrgency(false);
    }
  };

  const handleResetUrgency = async () => {
    setSavingUrgency(true);
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          follow_up_date: item.follow_up_date || "",
          follow_up_type: item.follow_up_type || "",
          follow_up_note: item.follow_up_note || "",
          urgency_override: "",
        }),
      });
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to reset urgency override"));
      }
      setUrgencyOverride("auto");
      await onRefresh();
      toast.success(`${item.title}: urgency reset`);
    } catch (error) {
      toast.error(error?.message || "Failed to reset urgency override");
    } finally {
      setSavingUrgency(false);
    }
  };

  const handleSaveLatest = async () => {
    if (!item.latest_contact_entry_id || !latestActionDraft) return;
    setSavingLatest(true);
    try {
      const response = await adminFetch(
        `/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/${encodeURIComponent(item.latest_contact_entry_id)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action_type: latestActionDraft,
            note: latestNoteDraft,
          }),
        }
      );
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to update latest contact action"));
      }
      setTimelineEntries((current) =>
        current.map((entry) => (entry.entry_id === item.latest_contact_entry_id ? payload.entry || entry : entry))
      );
      await onRefresh();
      setEditingLatest(false);
      toast.success(`${item.title}: latest contact updated`);
    } catch (error) {
      toast.error(error?.message || "Failed to update latest contact action");
    } finally {
      setSavingLatest(false);
    }
  };

  const handleDeleteLatest = async () => {
    if (!item.latest_contact_entry_id) return;
    const confirmed = window.confirm("Delete the latest contact log entry from this queue card?");
    if (!confirmed) return;

    setDeletingLatest(true);
    try {
      const response = await adminFetch(
        `/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/${encodeURIComponent(item.latest_contact_entry_id)}`,
        {
          method: "DELETE",
        }
      );
      const payload = await safeJsonParse(response, {});
      if (!response.ok) {
        throw new Error(formatApiError(payload, "Failed to delete latest contact action"));
      }
      setTimelineEntries([]);
      setTimelineLoaded(false);
      setTimelineError("");
      await onRefresh();
      setEditingLatest(false);
      toast.success(`${item.title}: latest contact deleted`);
    } catch (error) {
      toast.error(error?.message || "Failed to delete latest contact action");
    } finally {
      setDeletingLatest(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-[#11161d]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              aria-label={`Select ${item.title}`}
              checked={selected}
              onCheckedChange={(checked) => onToggleSelect(item.id, checked === true)}
            />
            <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
            <Badge variant="outline" className="capitalize">
              {item.lane.replace(/-/g, " ")}
            </Badge>
            {item.follow_up_status_label ? (
              <Badge variant="outline">{item.follow_up_status_label}</Badge>
            ) : null}
            {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
          </div>
          {outreachReasons.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {outreachReasons.slice(0, 3).map((reason) => (
                <Badge className="rounded-full" key={reason.key} variant="secondary">
                  {reason.label}
                </Badge>
              ))}
            </div>
          ) : null}
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {item.what_this_person_needs_now || item.next_move || item.reason}
          </p>
          {item.latest_contact_summary ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.latest_contact_summary}</p>
          ) : null}
          {item.next_follow_up_summary ? (
            <p className={`mt-1 text-xs ${appearance.accentTextClass}`}>{item.next_follow_up_summary}</p>
          ) : null}
          {item.contact_label ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.contact_label}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            disabled={disablePersonRouteActions || batchActive}
            onClick={() => onOpen(item.id)}
            size="sm"
            variant="outline"
          >
            Open
          </Button>
          {onOpenTimeline ? (
            <Button
              disabled={disablePersonRouteActions || batchActive}
              onClick={() => onOpenTimeline(item.id)}
              size="sm"
              variant="ghost"
            >
              Timeline
            </Button>
          ) : null}
        </div>
      </div>

      {disablePersonRouteActions && item.person_route_action_note ? (
        <p className="mt-3 rounded-xl border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-500 dark:border-white/10 dark:text-slate-400">
          {item.person_route_action_note}
        </p>
      ) : null}

      {hasContext ? (
        <div className="mt-3">
          <Button
            className="h-auto px-0 text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
            onClick={() => setShowContext((current) => !current)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {showContext ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
            {showContext ? "Hide context" : "Show context"}
          </Button>
        </div>
      ) : null}

      {showContext ? (
        <div className="mt-3 rounded-2xl border border-slate-200/80 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.03]">
          {item.display_detail_tags?.length ? (
            <div className="flex flex-wrap gap-2">
              {item.display_detail_tags.slice(0, 4).map((tag) => (
                <Badge className="rounded-full" key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}

          {hasDispositionContext ? (
            <div className="mt-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                Disposition
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                {item.stakeholder_disposition_previous_state.replace(/_/g, " ")} to{" "}
                {item.stakeholder_disposition_new_state.replace(/_/g, " ")}
              </p>
            </div>
          ) : null}

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {contextItems.map((entry) => (
              <div key={entry.label}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  {entry.label}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{entry.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Recent touches
            </p>
            {timelineLoading ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Loading recent touches</p>
            ) : timelineEntries.length ? (
              <div className="mt-2 space-y-2">
                {timelineEntries.map((entry) => (
                  <div
                    className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-[#0b1118]"
                    key={entry.entry_id}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{entry.action_label}</Badge>
                      {entry.created_at ? (
                        <span className="text-[11px] text-slate-500 dark:text-slate-400">
                          {formatDateTime(entry.created_at)}
                        </span>
                      ) : null}
                    </div>
                    {entry.note ? (
                      <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{entry.note}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : timelineLoaded ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">No contact history has been logged yet.</p>
            ) : null}
            {timelineError ? (
              <p className="mt-2 text-xs text-rose-600 dark:text-rose-300">{timelineError}</p>
            ) : null}
          </div>

          {canLoadDispositionPreview ? (
            <div className="mt-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                Recent disposition
              </p>
              {dispositionLoading ? (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Loading disposition history</p>
              ) : dispositionEntries.length ? (
                <div className="mt-2 space-y-2">
                  {dispositionEntries.map((entry) => (
                    <div
                      className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-[#0b1118]"
                      key={entry.event_id}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                          {formatDispositionState(entry.previous_state)} to {formatDispositionState(entry.new_state)}
                        </Badge>
                        {entry.changed_at ? (
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">
                            {formatDateTime(entry.changed_at)}
                          </span>
                        ) : null}
                      </div>
                      {entry.reason_label ? (
                        <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{entry.reason_label}</p>
                      ) : null}
                      {entry.note ? (
                        <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{entry.note}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : dispositionLoaded ? (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  No disposition history has been recorded yet.
                </p>
              ) : null}
              {dispositionError ? (
                <p className="mt-2 text-xs text-rose-600 dark:text-rose-300">{dispositionError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 border-t border-slate-200/80 pt-4 dark:border-white/10">
        <Textarea
          className="min-h-[72px]"
          onChange={(event) => setContactNote(event.target.value)}
          placeholder="Short note for the contact outcome"
          value={contactNote}
        />
        <div className="flex flex-wrap gap-2">
          {STAKEHOLDER_CONTACT_ACTIONS.map((action) => (
            <Button
              disabled={!!savingAction || savingFollowUp || savingUrgency || batchActive}
              key={action.value}
              onClick={() => handleLogAction(action.value)}
              size="sm"
              type="button"
              variant={savingAction === action.value ? "default" : "outline"}
            >
              {savingAction === action.value ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {action.label}
            </Button>
          ))}
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,180px)_minmax(0,180px)_auto]">
          <Select onValueChange={setFollowUpType} value={followUpType}>
            <SelectTrigger>
              <SelectValue placeholder="Follow-up type" />
            </SelectTrigger>
            <SelectContent>
              {STAKEHOLDER_FOLLOW_UP_TYPES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            onChange={(event) => setFollowUpDate(event.target.value)}
            type="date"
            value={followUpDate}
          />
          <Textarea
            className="min-h-[72px] md:col-span-2"
            onChange={(event) => setFollowUpNote(event.target.value)}
            placeholder="Short note for the follow-up plan"
            value={followUpNote}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={savingFollowUp || savingUrgency || batchActive}
              onClick={handleSaveFollowUp}
              type="button"
              variant="outline"
            >
              {savingFollowUp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Set follow-up
            </Button>
            <Button
              disabled={savingFollowUp || savingUrgency || batchActive}
              onClick={handleClearFollowUp}
              type="button"
              variant="outline"
            >
              Clear follow-up
            </Button>
          </div>
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,260px)_auto]">
          <Select onValueChange={setUrgencyOverride} value={urgencyOverride || "auto"}>
            <SelectTrigger>
              <SelectValue placeholder="Urgency override" />
            </SelectTrigger>
            <SelectContent>
              {STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={savingUrgency || savingFollowUp || batchActive}
              onClick={handleSaveUrgency}
              type="button"
              variant="outline"
            >
              {savingUrgency ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Set urgency
            </Button>
            <Button
              disabled={savingUrgency || savingFollowUp || batchActive}
              onClick={handleResetUrgency}
              type="button"
              variant="outline"
            >
              Reset urgency
            </Button>
          </div>
        </div>

        {item.latest_contact_entry_id ? (
          <div className="mt-4 rounded-2xl border border-slate-200/80 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Latest log
              </p>
              {editingLatest ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={!latestActionDraft || savingLatest || batchActive}
                    onClick={handleSaveLatest}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    {savingLatest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save latest
                  </Button>
                  <Button disabled={batchActive} onClick={() => setEditingLatest(false)} size="sm" type="button" variant="ghost">
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button disabled={batchActive} onClick={() => setEditingLatest(true)} size="sm" type="button" variant="ghost">
                    Edit latest
                  </Button>
                  <Button
                    disabled={deletingLatest || batchActive}
                    onClick={handleDeleteLatest}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {deletingLatest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Delete latest
                  </Button>
                </div>
              )}
            </div>

            {editingLatest ? (
              <div className="mt-3 space-y-2">
                <Select onValueChange={setLatestActionDraft} value={latestActionDraft}>
                  <SelectTrigger>
                    <SelectValue placeholder="Latest outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAKEHOLDER_CONTACT_ACTIONS.map((action) => (
                      <SelectItem key={action.value} value={action.value}>
                        {action.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  className="min-h-[72px]"
                  onChange={(event) => setLatestNoteDraft(event.target.value)}
                  placeholder="Edit the latest contact note"
                  value={latestNoteDraft}
                />
              </div>
            ) : (
              <div className="mt-3 space-y-1">
                {item.latest_contact_label ? (
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{item.latest_contact_label}</p>
                ) : null}
                {item.latest_contact_note ? (
                  <p className="text-xs leading-5 text-slate-600 dark:text-slate-300">{item.latest_contact_note}</p>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400">No note on the latest contact log.</p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export default function StakeholderOutreachQueue({ fetchRemoteState, items, onOpenItem, onOpenTimeline }) {
  const adminFetch = useAdminFetch();
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkFollowUpDate, setBulkFollowUpDate] = useState("");
  const [bulkFollowUpType, setBulkFollowUpType] = useState("");
  const [bulkFollowUpNote, setBulkFollowUpNote] = useState("");
  const [bulkUrgencyOverride, setBulkUrgencyOverride] = useState("auto");
  const [bulkContactAction, setBulkContactAction] = useState("");
  const [bulkContactNote, setBulkContactNote] = useState("");
  const [applyingBulk, setApplyingBulk] = useState(false);
  const queueCandidates = useMemo(
    () => sortItemsGlobally(items.filter((item) => isOutreachQueueItem(item))),
    [items]
  );
  const filterCounts = useMemo(
    () =>
      OUTREACH_QUEUE_FILTERS.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.value]:
            filter.value === "all"
              ? queueCandidates.length
              : queueCandidates.filter((item) => matchesOutreachQueueFilter(item, filter.value)).length,
        }),
        {}
      ),
    [queueCandidates]
  );
  const queueItems = useMemo(
    () =>
      queueCandidates
        .filter((item) => matchesOutreachQueueFilter(item, activeFilter))
        .slice(0, 6),
    [activeFilter, queueCandidates]
  );
  const visibleQueueIds = useMemo(() => queueItems.map((item) => item.id), [queueItems]);
  const queueCandidateIds = useMemo(() => new Set(queueCandidates.map((item) => item.id)), [queueCandidates]);
  const selectedItems = useMemo(
    () => queueCandidates.filter((item) => selectedIds.includes(item.id)),
    [queueCandidates, selectedIds]
  );
  const activeFilterLabel =
    OUTREACH_QUEUE_FILTERS.find((filter) => filter.value === activeFilter)?.label || "All";

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => queueCandidateIds.has(id)));
  }, [queueCandidateIds]);

  const toggleSelectedId = useCallback((itemId, nextSelected) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (nextSelected) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return Array.from(next);
    });
  }, []);

  const handleSelectVisible = useCallback(() => {
    setSelectedIds((current) => Array.from(new Set([...current, ...visibleQueueIds])));
  }, [visibleQueueIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleApplyBulk = useCallback(async () => {
    if (!selectedItems.length) return;
    setApplyingBulk(true);
    let updatedCount = 0;
    const failed = [];
    try {
      for (const item of selectedItems) {
        try {
          const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              follow_up_date: bulkFollowUpDate,
              follow_up_type: bulkFollowUpType,
              follow_up_note: bulkFollowUpNote,
              urgency_override: bulkUrgencyOverride === "auto" ? "" : bulkUrgencyOverride,
            }),
          });
          const payload = await safeJsonParse(response, {});
          if (!response.ok) {
            throw new Error(formatApiError(payload, "Failed to apply bulk stakeholder update"));
          }
          updatedCount += 1;
        } catch (error) {
          failed.push(item.title || item.id);
        }
      }
      await fetchRemoteState();
      if (!failed.length) {
        toast.success(`Bulk update complete: updated ${updatedCount}, failed 0`);
      } else {
        toast.error(`Bulk update complete: updated ${updatedCount}, failed ${failed.length}`);
      }
      setSelectedIds([]);
    } finally {
      setApplyingBulk(false);
    }
  }, [
    adminFetch,
    bulkFollowUpDate,
    bulkFollowUpNote,
    bulkFollowUpType,
    bulkUrgencyOverride,
    fetchRemoteState,
    selectedItems,
  ]);

  const handleApplyBulkContact = useCallback(async () => {
    if (!selectedItems.length || !bulkContactAction) return;
    setApplyingBulk(true);
    let updatedCount = 0;
    const failed = [];
    try {
      for (const item of selectedItems) {
        try {
          const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              lane: item.lane,
              title_at_time: item.title,
              action_type: bulkContactAction,
              note: bulkContactNote,
              contact_value: item.contact_label || "",
            }),
          });
          const payload = await safeJsonParse(response, {});
          if (!response.ok) {
            throw new Error(formatApiError(payload, "Failed to apply bulk contact action"));
          }
          updatedCount += 1;
        } catch (error) {
          failed.push(item.title || item.id);
        }
      }
      await fetchRemoteState();
      if (!failed.length) {
        toast.success(`Bulk contact complete: updated ${updatedCount}, failed 0`);
      } else {
        toast.error(`Bulk contact complete: updated ${updatedCount}, failed ${failed.length}`);
      }
      setSelectedIds([]);
      setBulkContactAction("");
      setBulkContactNote("");
    } finally {
      setApplyingBulk(false);
    }
  }, [adminFetch, bulkContactAction, bulkContactNote, fetchRemoteState, selectedItems]);

  const handleBulkClearFollowUp = useCallback(async () => {
    if (!selectedItems.length) return;
    setApplyingBulk(true);
    let updatedCount = 0;
    const failed = [];
    try {
      for (const item of selectedItems) {
        try {
          const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              follow_up_date: "",
              follow_up_type: "",
              follow_up_note: "",
              urgency_override: item.urgency_override === "auto" ? "" : (item.urgency_override || ""),
            }),
          });
          const payload = await safeJsonParse(response, {});
          if (!response.ok) {
            throw new Error(formatApiError(payload, "Failed to clear bulk follow-up"));
          }
          updatedCount += 1;
        } catch (error) {
          failed.push(item.title || item.id);
        }
      }
      await fetchRemoteState();
      if (!failed.length) {
        toast.success(`Bulk clear complete: updated ${updatedCount}, failed 0`);
      } else {
        toast.error(`Bulk clear complete: updated ${updatedCount}, failed ${failed.length}`);
      }
      setSelectedIds([]);
      setBulkFollowUpDate("");
      setBulkFollowUpType("");
      setBulkFollowUpNote("");
    } finally {
      setApplyingBulk(false);
    }
  }, [adminFetch, fetchRemoteState, selectedItems]);

  const handleBulkResetUrgency = useCallback(async () => {
    if (!selectedItems.length) return;
    setApplyingBulk(true);
    let updatedCount = 0;
    const failed = [];
    try {
      for (const item of selectedItems) {
        try {
          const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(item.id)}/state`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              follow_up_date: item.follow_up_date || "",
              follow_up_type: item.follow_up_type || "",
              follow_up_note: item.follow_up_note || "",
              urgency_override: "",
            }),
          });
          const payload = await safeJsonParse(response, {});
          if (!response.ok) {
            throw new Error(formatApiError(payload, "Failed to reset bulk urgency"));
          }
          updatedCount += 1;
        } catch (error) {
          failed.push(item.title || item.id);
        }
      }
      await fetchRemoteState();
      if (!failed.length) {
        toast.success(`Bulk urgency reset complete: updated ${updatedCount}, failed 0`);
      } else {
        toast.error(`Bulk urgency reset complete: updated ${updatedCount}, failed ${failed.length}`);
      }
      setSelectedIds([]);
      setBulkUrgencyOverride("auto");
    } finally {
      setApplyingBulk(false);
    }
  }, [adminFetch, fetchRemoteState, selectedItems]);

  return (
    <section className="rounded-[26px] border border-slate-200/80 bg-white/85 p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.03]">
      <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
        <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Outreach Queue</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          The clearest people to contact next, ordered from shared follow-up state, urgency override, and lane priority.
        </p>
        <p className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {filterCounts.all || 0} people surfaced
          {activeFilter !== "all" ? ` • showing ${activeFilterLabel}` : ""}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {OUTREACH_QUEUE_FILTERS.map((filter) => {
            const isActive = activeFilter === filter.value;
            return (
              <Button
                className="rounded-full"
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                size="sm"
                type="button"
                variant={isActive ? "default" : "outline"}
              >
                {filter.label}
                <span className="rounded-full bg-black/10 px-1.5 py-0.5 text-[10px] dark:bg-white/15">
                  {filterCounts[filter.value] || 0}
                </span>
              </Button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={!visibleQueueIds.length || applyingBulk} onClick={handleSelectVisible} size="sm" type="button" variant="outline">
            Select visible
          </Button>
          <Button disabled={!selectedIds.length || applyingBulk} onClick={handleClearSelection} size="sm" type="button" variant="outline">
            Clear selection
          </Button>
          {selectedIds.length ? (
            <p className="self-center text-xs text-slate-500 dark:text-slate-400">
              {selectedIds.length} selected
            </p>
          ) : null}
        </div>
      </div>

      {selectedIds.length ? (
        <div className="mt-4 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Bulk queue actions</p>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Apply the same follow-up plan and urgency override to {selectedIds.length} selected people.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button disabled={applyingBulk} onClick={handleApplyBulk} size="sm" type="button">
                {applyingBulk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Apply state to selected
              </Button>
              <Button disabled={applyingBulk} onClick={handleBulkClearFollowUp} size="sm" type="button" variant="outline">
                Clear follow-up
              </Button>
              <Button disabled={applyingBulk} onClick={handleBulkResetUrgency} size="sm" type="button" variant="outline">
                Reset urgency
              </Button>
            </div>
          </div>
          <div className="mt-3 rounded-xl border border-slate-200/80 bg-white/80 p-3 dark:border-white/10 dark:bg-[#0b1118]">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Bulk contact log
            </p>
            <div className="mt-2 grid gap-2 md:grid-cols-[minmax(0,200px)_1fr_auto]">
              <Select onValueChange={setBulkContactAction} value={bulkContactAction || "none"}>
                <SelectTrigger aria-label="Bulk contact outcome">
                  <SelectValue placeholder="Contact outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No outcome</SelectItem>
                  {STAKEHOLDER_CONTACT_ACTIONS.map((action) => (
                    <SelectItem key={action.value} value={action.value}>
                      {action.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                className="min-h-[72px]"
                onChange={(event) => setBulkContactNote(event.target.value)}
                placeholder="Bulk contact note. This will create a real contact-log entry for each selected person."
                value={bulkContactNote}
              />
              <Button
                disabled={applyingBulk || !bulkContactAction}
                onClick={handleApplyBulkContact}
                size="sm"
                type="button"
                variant="outline"
              >
                {applyingBulk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Log contact for selected
              </Button>
            </div>
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,180px)_minmax(0,180px)]">
            <Select onValueChange={(value) => setBulkFollowUpType(value === "none" ? "" : value)} value={bulkFollowUpType || "none"}>
              <SelectTrigger>
                <SelectValue placeholder="Follow-up type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No type</SelectItem>
                {STAKEHOLDER_FOLLOW_UP_TYPES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              onChange={(event) => setBulkFollowUpDate(event.target.value)}
              type="date"
              value={bulkFollowUpDate}
            />
          </div>
          <Textarea
            className="mt-2 min-h-[72px]"
            onChange={(event) => setBulkFollowUpNote(event.target.value)}
            placeholder="Bulk follow-up note. Leave blank to clear."
            value={bulkFollowUpNote}
          />
          <div className="mt-2 grid gap-2 md:grid-cols-[minmax(0,280px)_auto]">
            <Select onValueChange={setBulkUrgencyOverride} value={bulkUrgencyOverride || "auto"}>
              <SelectTrigger>
                <SelectValue placeholder="Urgency override" />
              </SelectTrigger>
              <SelectContent>
                {STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="self-center text-xs text-slate-500 dark:text-slate-400">
              Blank follow-up fields clear shared follow-up state. `Automatic` resets urgency.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {queueItems.length ? (
          queueItems.map((item) => (
            <OutreachQueueCard
              batchActive={applyingBulk}
              item={item}
              key={item.id}
              onOpen={onOpenItem}
              onOpenTimeline={onOpenTimeline}
              onRefresh={fetchRemoteState}
              onToggleSelect={toggleSelectedId}
              selected={selectedIds.includes(item.id)}
            />
          ))
        ) : (
          <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
            No {activeFilterLabel.toLowerCase()} outreach items are surfaced right now.
          </p>
        )}
      </div>
    </section>
  );
}
